/**
 * TopBar — Fixed top navigation bar (48px).
 * LEFT: AWENI logo + current module name
 * RIGHT: notification bell (badge) + profile avatar (initials)
 * Collapses on scroll down, reappears on scroll up.
 */

import type { AweniContext } from '../context/AweniContext';
import { NativeService } from '../services/NativeService';
import { mountChipRow, setChipRowSurface, setChipRowTop } from './ChipRow';
import { syncEngine } from '../services/SyncEngine';

interface TopBarCallbacks {
  onNotificationsClick: () => void;
  onProfileClick: () => void;
}

let _bar: HTMLElement | null = null;
let _moduleLabel: HTMLSpanElement | null = null;
let _subLabel: HTMLSpanElement | null = null;
let _subSep: HTMLSpanElement | null = null;
let _subLabelBackHandler: (() => void) | null = null;
let _bellBadge: HTMLSpanElement | null = null;
let _avatarBtn: HTMLButtonElement | null = null;
let _ctx: AweniContext | null = null;
let _lastScrollY = 0;
let _hidden = false;
let _progressBar: HTMLDivElement | null = null;

export function mountTopBar(
  container: HTMLElement,
  ctx: AweniContext,
  callbacks: TopBarCallbacks,
): void {
  _ctx = ctx;

  // Sync native status bar style with current theme
  NativeService.setStatusBarStyle(ctx.theme.isDark ? 'dark' : 'light');

  const bar = container;
  bar.setAttribute('role', 'banner');
  bar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--ln-chrome-height, 48px);
    z-index: 100;
    background: color-mix(in srgb, var(--ln-bg-primary) 85%, transparent);
    backdrop-filter: blur(12px) saturate(1.4);
    -webkit-backdrop-filter: blur(12px) saturate(1.4);
    border-bottom: 1px solid color-mix(in srgb, var(--ln-accent) 15%, transparent);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--ln-space-3, 12px);
    transition: transform var(--motion-duration-300) var(--motion-ease-standard);
    box-shadow: var(--ln-shadow-sm);
  `;
  _bar = bar;

  // Progress bar hairline Champagne
  const progress = document.createElement('div');
  progress.style.cssText = `
    position: absolute;
    bottom: -1px;
    left: 0;
    height: 2px;
    width: 0%;
    background: var(--ln-accent, #9C8348);
    transition: width 100ms var(--motion-ease-standard);
    display: none;
  `;
  bar.appendChild(progress);
  _progressBar = progress;

  mountChipRow(ctx);
  setChipRowTop(48);
  setChipRowSurface('home');

  // --- Left: logo + module name ---
  // The brand cluster (logo + wordmark + room separator + room label) is a
  // single clickable region that always navigates to the home lobby — same
  // affordance as Apple's left-edge "Apps" button or Google's app drawer.
  // Users learn this in seconds and rely on it across every module page.
  const left = document.createElement('button');
  left.type = 'button';
  left.setAttribute('aria-label', ctx.i18n.t('nav_home') || 'Home');
  left.style.cssText = `
    display:flex;align-items:center;gap:var(--ln-space-2, 8px);min-width:0;
    background:transparent;border:none;cursor:pointer;
    padding:var(--ln-space-1, 4px) var(--ln-space-2, 8px);margin-inline-start:calc(-1 * var(--ln-space-2, 8px));border-radius:var(--ln-radius-sm, 8px);
    transition:background var(--motion-duration-fast) var(--motion-easing-default);
  `;
  left.addEventListener('mouseenter', () => { left.style.background = 'color-mix(in srgb, var(--ln-accent) 4%, transparent)'; });
  left.addEventListener('mouseleave', () => { left.style.background = 'transparent'; });
  left.addEventListener('focus', () => { left.style.outline = '2px solid var(--accent)'; left.style.outlineOffset = '2px'; });
  left.addEventListener('blur', () => { left.style.outline = 'none'; });
  left.addEventListener('click', () => {
    // Navigate to the home lobby. Use hash so the SPA router handles it
    // without a full page reload (and without re-arming the SW lifecycle).
    if (location.hash !== '#/home') {
      location.hash = '#/home';
    }
  });

  // Brand v1: real monogram image, no wordmark (per docs/BRAND.md). The
  // mark fills ~32px in the 48px topbar — visually equivalent to the old
  // "AWENI" text but reinforces the visual identity instead of repeating
  // the name. The dark theme uses the same PNG: midnight on transparent
  // reads as midnight on cream (light) AND as the silhouette void against
  // dark bg (the cream-on-midnight inverse is the maskable variant for
  // installed icons; in-app we want the literal mark).
  const logo = document.createElement('img');
  logo.src = '/aweni/icons/pwa-192x192.png';
  logo.alt = 'AWENI';
  logo.width = 32;
  logo.height = 32;
  logo.setAttribute('aria-label', 'AWENI');
  logo.style.cssText = `
    display: block;
    flex-shrink: 0;
    border-radius: var(--ln-radius-sm);
  `;
  // No filter:invert needed \u2014 logo is champagne #9C8348 which reads on both
  // cream (3.4:1) and midnight (5.4:1) backgrounds.

  // Wordmark \u2014 small uppercase AWENI, reinforces brand on every screen.
  const wordmark = document.createElement('span');
  wordmark.textContent = 'AWENI';
  wordmark.style.cssText = `
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 1.5px;
    color: var(--text-primary);
    flex-shrink: 0;
  `;

  // Left button contains ONLY logo + wordmark \u2192 navigates home.
  // The breadcrumb trail lives outside so each crumb is individually tappable.
  left.append(logo, wordmark);

  // \u2500\u2500 Breadcrumb trail \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  // Structure: [\u203a] [Module] [\u203a] [Sub-section (optional)]
  // Module crumb = text label (or accent link when sub-section is active)
  // Sub-section crumb = only shown when a module exposes a deep section
  const crumbBar = document.createElement('div');
  crumbBar.style.cssText = `
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  `;

  const crumbSep = document.createElement('span');
  crumbSep.textContent = '\u203a';
  crumbSep.setAttribute('aria-hidden', 'true');
  crumbSep.style.cssText = 'color:var(--text-secondary);font-size:12px;flex-shrink:0;opacity:0.4;';

  const label = document.createElement('span');
  label.style.cssText = `
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    letter-spacing: 0.2px;
    border-radius: 4px;
    padding: 1px 3px;
    transition: color var(--motion-duration-fast) var(--motion-easing-default),
                background var(--motion-duration-fast) var(--motion-easing-default);
  `;
  label.textContent = _resolveRoomName('home', ctx);
  _moduleLabel = label;

  // Sub-section crumb \u2014 hidden until setTopBarSubSection() is called
  const subSep = document.createElement('span');
  subSep.textContent = '\u203a';
  subSep.setAttribute('aria-hidden', 'true');
  subSep.style.cssText = 'color:var(--text-secondary);font-size:12px;flex-shrink:0;opacity:0.4;display:none;';
  _subSep = subSep;

  const subLabel = document.createElement('span');
  subLabel.style.cssText = `
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: none;
  `;
  _subLabel = subLabel;

  crumbBar.append(crumbSep, label, subSep, subLabel);

  // --- Right: bell + avatar ---
  const right = document.createElement('div');
  right.style.cssText = 'display:flex;align-items:center;gap:var(--ln-space-2, 8px);flex-shrink:0;';

  // Sync Status Badge
  const syncBadge = document.createElement('div');
  syncBadge.className = 'aweni-sync-badge';
  syncBadge.style.cssText = `
    display: none;
    align-items: center;
    gap: var(--ln-space-1, 4px);
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: var(--ln-radius-sm, 8px);
    transition: all 300ms var(--motion-ease-standard);
    flex-shrink: 0;
  `;

  let wasOffline = !ctx.offline.isOnline;
  let savedTimeout: number | null = null;

  const updateSyncBadge = async () => {
    if (!syncBadge) return;
    const isOnline = ctx.offline.isOnline;
    const status = await syncEngine.getStatus();
    const pendingCount = status.pendingCount;
    const isSyncing = status.syncInProgress;

    // Check transition from offline to online (back online)
    if (isOnline && wasOffline && pendingCount === 0) {
      wasOffline = false;
      if (savedTimeout) window.clearTimeout(savedTimeout);
      
      syncBadge.textContent = 'Saved';
      syncBadge.className = 'aweni-sync-badge aweni-sync-badge--saved';
      syncBadge.style.display = 'flex';
      
      savedTimeout = window.setTimeout(() => {
        syncBadge.style.display = 'none';
        savedTimeout = null;
      }, 3000);
      return;
    }

    wasOffline = !isOnline;

    if (!isOnline) {
      if (savedTimeout) {
        window.clearTimeout(savedTimeout);
        savedTimeout = null;
      }
      if (pendingCount > 0) {
        syncBadge.textContent = `${pendingCount} pending`;
        syncBadge.className = 'aweni-sync-badge aweni-sync-badge--offline-pending';
        syncBadge.style.display = 'flex';
      } else {
        syncBadge.textContent = 'Offline';
        syncBadge.className = 'aweni-sync-badge aweni-sync-badge--offline';
        syncBadge.style.display = 'flex';
      }
    } else if (isSyncing) {
      if (savedTimeout) {
        window.clearTimeout(savedTimeout);
        savedTimeout = null;
      }
      syncBadge.innerHTML = 'Syncing...<span class="aweni-sync-badge__dot"></span>';
      syncBadge.className = 'aweni-sync-badge aweni-sync-badge--syncing';
      syncBadge.style.display = 'flex';
    } else {
      if (!savedTimeout) {
        syncBadge.style.display = 'none';
      }
    }
  };

  ctx.offline.onStatusChange(() => {
    updateSyncBadge();
  });

  syncEngine.on('sync-start', () => {
    updateSyncBadge();
  });

  syncEngine.on('sync-complete', () => {
    updateSyncBadge();
  });

  syncEngine.on('action-synced', () => {
    updateSyncBadge();
  });

  // Run immediately
  updateSyncBadge();

  // Notification bell button — WCAG 2.1 AA: stroke + size + hit-target
  // explicitly tuned to pass 1.4.11 non-text 3:1 contrast in BOTH themes.
  // (Was 2px stroke on 20×20 with currentColor inherited from a
  // var(--text-primary) inline style — observed as low-contrast in
  // Firefox light mode.) We now use a class with theme-specific color
  // set in CSS so it's explicit and overridable.
  const bellBtn = document.createElement('button');
  bellBtn.className = 'aweni-topbar-icon-btn';
  bellBtn.setAttribute('aria-label', ctx.i18n.t('topbar_notifications'));
  // NOTE: do NOT set `background: transparent` or `color: ...` inline here —
  // both must come from the .aweni-topbar-icon-btn class so the theme toggle
  // and the .dark prefix actually take effect. An inline `background: transparent`
  // here used to defeat the visible pill bg + made the bell read as white-on-white
  // in light mode (Firefox cascaded the body's text-white into the SVG stroke
  // via `currentColor`).
  bellBtn.style.cssText = `
    position: relative;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ln-radius-md, 12px);
    border: none;
    cursor: pointer;
    transition: background var(--motion-duration-fast) var(--motion-easing-default);
  `;
  bellBtn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>`;

  const badge = document.createElement('span');
  badge.style.cssText = `
    position: absolute;
    top: var(--ln-space-1, 4px);
    inset-inline-end: var(--ln-space-1, 4px);
    min-width: var(--ln-space-4, 16px);
    height: var(--ln-space-4, 16px);
    padding: 0 var(--ln-space-1, 4px);
    border-radius: var(--ln-radius-sm, 8px);
    background: var(--notification-dot);
    color: var(--ln-accent-text, #fff);
    font-size: 10px;
    font-weight: 700;
    display: none;
    align-items: center;
    justify-content: center;
    line-height: 1;
  `;
  _bellBadge = badge;
  bellBtn.appendChild(badge);
  bellBtn.addEventListener('click', callbacks.onNotificationsClick);

  // Profile avatar button. The .aweni-topbar-avatar class opts out of the
  // global 44×44 button min-size — that minimum was inflating the avatar to
  // 44×44 in a 48px topbar, leaving 2px of breathing room and making the
  // circle visually clip the topbar's border-bottom into the banner below.
  const avatarBtn = document.createElement('button');
  avatarBtn.className = 'aweni-topbar-avatar';
  avatarBtn.setAttribute('aria-label', ctx.i18n.t('topbar_profile'));
  // The text color MUST be `var(--accent-text)` not `#fff`. Brand v1 makes
  // --accent invert per theme (black in light, cream in dark); a hardcoded
  // white initial reads as cream-on-cream in dark mode → invisible avatar.
  // --accent-text is the paired token that always contrasts with --accent.
  avatarBtn.style.cssText = `
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--accent-text);
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    flex-shrink: 0;
  `;
  _updateAvatarText(avatarBtn, ctx);
  _avatarBtn = avatarBtn;
  avatarBtn.addEventListener('click', callbacks.onProfileClick);

  right.append(syncBadge, bellBtn, avatarBtn);
  bar.append(left, crumbBar, right);

  // Initial badge update
  _refreshBadge(ctx);

  // Scroll collapse — watch the main viewport
  const viewport = document.getElementById('aweni-main');
  if (viewport) {
    viewport.addEventListener('scroll', () => {
      const y = viewport.scrollTop;
      if (y > _lastScrollY && y > 48 && !_hidden) {
        _hidden = true;
        if (_bar) _bar.style.transform = 'translateY(-100%)';
        setChipRowTop(0);
      } else if (y < _lastScrollY && _hidden) {
        _hidden = false;
        if (_bar) _bar.style.transform = 'translateY(0)';
        setChipRowTop(48);
      }
      _lastScrollY = y;

      // Update scroll progress bar
      if (_progressBar && _progressBar.style.display !== 'none') {
        const maxScroll = viewport.scrollHeight - viewport.clientHeight;
        if (maxScroll > 0) {
          const pct = Math.min(100, Math.max(0, (y / maxScroll) * 100));
          _progressBar.style.width = `${pct}%`;
        } else {
          _progressBar.style.width = '0%';
        }
      }
    }, { passive: true });
  }
}

/**
 * Update the room name shown in the top bar after the AWENI wordmark.
 * Uses _resolveRoomName which prefers proper nouns (BalanceTab, Memoriz,
 * SelectO, Yawmi) for branded rooms and i18n nav_* labels for the rest.
 */
export function setTopBarModuleName(moduleId: string): void {
  if (_moduleLabel && _ctx) {
    _moduleLabel.textContent = _resolveRoomName(moduleId, _ctx);
  }
  setChipRowSurface(moduleId === 'memoriz' ? 'hisni' : moduleId);
  // Clear any sub-section crumb when navigating to a new module
  clearTopBarSubSection();

  // Show progress bar only for readable modules (memoriz, hisni, quran, blog)
  if (_progressBar) {
    const readable = ['memoriz', 'hisni', 'quran', 'blog'].includes(moduleId);
    _progressBar.style.display = readable ? 'block' : 'none';
    _progressBar.style.width = '0%';
  }
}

/**
 * Show a sub-section crumb in the TopBar breadcrumb trail.
 * e.g. setTopBarSubSection('Prayer Settings', () => navigate('/more/settings'))
 * results in: AWENI › Settings › Prayer Settings
 * Clicking "Settings" triggers onBack (if provided).
 */
export function setTopBarSubSection(label: string, onBack?: () => void): void {
  if (!_subLabel || !_subSep || !_moduleLabel) return;
  _subSep.style.display = '';
  _subLabel.style.display = '';
  
  // Slide-in entering animation from bottom
  _subLabel.style.transition = 'none';
  _subLabel.style.transform = 'translateY(8px)';
  _subLabel.style.opacity = '0';
  _subLabel.textContent = label;
  // Force layout reflow
  _subLabel.offsetHeight;
  _subLabel.style.transition = 'transform var(--motion-duration-200) var(--motion-ease-decelerate), opacity var(--motion-duration-200) var(--motion-ease-decelerate)';
  _subLabel.style.transform = 'translateY(0)';
  _subLabel.style.opacity = '1';

  // Make module label a tappable back-link when a sub-section is active
  if (onBack) {
    _subLabelBackHandler = onBack;
    _moduleLabel.style.cursor = 'pointer';
    _moduleLabel.style.color = 'var(--accent, #0A0A0A)';
    _moduleLabel.style.textDecoration = 'underline';
    _moduleLabel.style.textDecorationColor = 'color-mix(in srgb, var(--accent) 40%, transparent)';
    _moduleLabel.setAttribute('role', 'button');
    _moduleLabel.setAttribute('tabindex', '0');
    _moduleLabel.addEventListener('click', _subLabelBackHandler);
    _moduleLabel.addEventListener('keydown', _subLabelBackKeyHandler);
  }
}

function _subLabelBackKeyHandler(e: KeyboardEvent): void {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    _subLabelBackHandler?.();
  }
}

/** Remove the sub-section crumb and restore module label to plain text. */
export function clearTopBarSubSection(): void {
  if (!_subLabel || !_subSep || !_moduleLabel) return;

  if (_subLabel.style.display !== 'none') {
    // Slide-out exiting animation towards top
    _subLabel.style.transition = 'transform var(--motion-duration-150) var(--motion-ease-accelerate), opacity var(--motion-duration-150) var(--motion-ease-accelerate)';
    _subLabel.style.transform = 'translateY(-8px)';
    _subLabel.style.opacity = '0';
    const subLabelRef = _subLabel;
    const subSepRef = _subSep;
    setTimeout(() => {
      subSepRef.style.display = 'none';
      subLabelRef.style.display = 'none';
      subLabelRef.textContent = '';
      subLabelRef.style.transform = '';
    }, 150);
  } else {
    _subSep.style.display = 'none';
    _subLabel.style.display = 'none';
    _subLabel.textContent = '';
  }

  if (_subLabelBackHandler) {
    _moduleLabel.removeEventListener('click', _subLabelBackHandler);
    _moduleLabel.removeEventListener('keydown', _subLabelBackKeyHandler);
    _subLabelBackHandler = null;
  }
  _moduleLabel.style.cursor = '';
  _moduleLabel.style.color = '';
  _moduleLabel.style.textDecoration = '';
  _moduleLabel.removeAttribute('role');
  _moduleLabel.removeAttribute('tabindex');
}

/**
 * Map a module ID to its display name in the topbar breadcrumb.
 * Proper nouns (BalanceTab, Memoriz, SelectO, Yawmi) stay in Latin script
 * across all 5 languages — they are product names, not translatable words.
 * Generic modules use the i18n nav_* labels.
 */
function _resolveRoomName(moduleId: string, ctx: AweniContext): string {
  // Branded rooms — proper nouns, never translated.
  // 2026-05-08: Memoriz → Hisni rename per ST3. Hisni is the room name (the
  // Islamic library + practice tools). The grid tile still says "Quran"
  // because that's the feature, not the room.
  const PROPER_NAMES: Record<string, string> = {
    home: 'Lobby',
    balance: 'BalanceTab',
    money: 'BalanceTab',
    balancetab: 'BalanceTab',
    quran: 'Hisni',
    memoriz: 'Hisni',
    hisni: 'Hisni',
    selecto: 'SelectO',
    yawmi: 'Yawmi',
  };
  if (PROPER_NAMES[moduleId]) return PROPER_NAMES[moduleId];
  // Fallback to i18n nav_* label, or the module id itself
  const key = `nav_${moduleId}`;
  const translated = ctx.i18n.t(key);
  return translated !== key ? translated : moduleId;
}

/**
 * Refresh the unread notification badge count.
 */
export function refreshTopBarBadge(): void {
  if (_ctx) _refreshBadge(_ctx);
}

/**
 * Refresh the profile avatar initials.
 */
export function refreshTopBarAvatar(): void {
  if (_avatarBtn && _ctx) _updateAvatarText(_avatarBtn, _ctx);
}

function _refreshBadge(ctx: AweniContext): void {
  if (!_bellBadge) return;
  const count = ctx.notifications.unreadCount;
  if (count > 0) {
    _bellBadge.textContent = count > 99 ? '99+' : String(count);
    _bellBadge.style.display = 'flex';
  } else {
    _bellBadge.style.display = 'none';
  }
}

function _updateAvatarText(btn: HTMLButtonElement, ctx: AweniContext): void {
  const profile = ctx.auth.activeProfile;
  const user = ctx.auth.user;
  const name = profile?.name || user?.name || '?';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2);
  btn.textContent = initials.toUpperCase() || '?';
}
